import { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { apiGetRequest, apiPostRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import { useSnackbar } from "../../../Components/SnackBar";
import ConfirmationModal from "../ConfirmationModal";
import MyMembersCard from "../../../Components/MyMembersCard";
import MembersHeader from "./components/MembersHeader";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { useDebounce } from "../../../hooks/useDebounce";
import { DEBOUNCE_DELAY, API_RECORDS_COUNT } from "../../../Constants/config";
import { parseDate } from "../../../Helpers/formating";
import { AppContext } from "../../../Context/AppContext";
import { useTranslation } from "react-i18next";

const DEFAULT_SORT_ORDER = "desc";

function CommunityMembers({ memberStats, getMemberStats }) {
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const initialTab = Number(searchParams.get("tab")) || 1;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [actionType, setActionType] = useState(""); // Stores the action type (remove, accept, reject)
  const [selectedMember, setSelectedMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showSnackbar = useSnackbar();
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);

  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);

  const [maxPage, setMaxPage] = useState({
    1: 1,
    2: 1,
  });

  const { sidebarData } = useContext(AppContext);
  const isModerator = sidebarData.isModerator;

  const { t } = useTranslation("page-my-community-members");

  const { debouncedValue: debouncedSearch } = useDebounce(
    searchValue,
    DEBOUNCE_DELAY,
  );

  const getMembers = async (params) => {
    const { activeTab, debouncedSearch = "", activePage } = params;
    try {
      if (activePage === 1) setIsLoading(true);
      const response = await apiGetRequest(
        endpoints.communityMembers({
          pageNumber: activePage,
          pageSize: API_RECORDS_COUNT,
          search: debouncedSearch,
          status: activeTab === 1 ? "ACTIVE" : "PENDING",
          sortBy: "join_date",
          sortOrder: DEFAULT_SORT_ORDER,
        }),
      );

      const newMembers = response?.data?.members || [];
      const maxPage = response?.data?.max_page_number ?? 1;

      if (activeTab === 1) {
        if (activePage === 1) {
          setMembers(newMembers);
        } else {
          setMembers((prevMembers) => {
            const allMembers = [...prevMembers, ...newMembers];
            return allMembers;
          });
        }

        setMaxPage((prev) => ({
          ...prev,
          1: maxPage,
        }));
      } else {
        if (activePage === 1) {
          setRequests(newMembers);
        } else {
          setRequests((prevRequests) => {
            const allRequests = [...prevRequests, ...newMembers];
            return allRequests;
          });
        }
        setMaxPage((prev) => ({ ...prev, 2: maxPage }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMembersCallback = useCallback(
    async (activePage) => {
      await getMembers({
        activeTab,
        debouncedSearch,
        activePage,
      });
    },
    [activeTab, debouncedSearch],
  );

  const { setContainerRef, resetPagination } = useInfiniteScroll({
    fetchMore: fetchMembersCallback,
    maxPage: maxPage[activeTab],
    threshold: 200,
    initialPage: 1,
  });

  useEffect(() => {
    resetPagination();
    fetchMembersCallback(1);
  }, [fetchMembersCallback, resetPagination]);

  useEffect(() => {
    getMemberStats();
  }, [activeTab, getMemberStats]);

  useEffect(() => {
    document.body.style.overflow = isBelowMd ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isBelowMd]);

  // remove member from community
  const removeMember = async () => {
    try {
      await apiPostRequest(endpoints.removeCommunityMember(selectedMember?.id));
      setMembers((prevMembers) =>
        prevMembers.filter((member) => member.id !== selectedMember.id),
      );
      showSnackbar(
        `${t("ConfirmationModal.text.has_been_removed_part1")} ${
          selectedMember?.full_name
        } ${t("ConfirmationModal.text.has_been_removed_part2")}`,
        "success",
      );
      getMemberStats();
    } catch (ex) {
      console.log("exception occurred while removing a member", ex);
    } finally {
      setIsModalOpen(false);
      setSelectedMember(null);
    }
  };

  // Accept member join request to community
  const acceptMember = async (member) => {
    try {
      await apiPostRequest(endpoints.approveCommunityMember(member.id));
      setRequests((prevMembers) =>
        prevMembers.filter((m) => m.id !== member.id),
      );
      setMembers((prevMembers) => {
        const updatedMembers = [...prevMembers, member];
        updatedMembers.sort((a, b) => a.id - b.id); // Sort the array by ID
        return updatedMembers;
      });
      showSnackbar(
        `${t("ConfirmationModal.text.has_been_accepted_part1")} ${
          member?.full_name
        } ${t("ConfirmationModal.text.has_been_accepted_part2")}`,
        "success",
      );
      getMemberStats();
    } catch (ex) {
      console.log("error occurred while accepting a member", ex);
    } finally {
      setSelectedMember(null);
    }
  };

  // Reject member join request to community
  const rejectMember = async () => {
    try {
      setIsLoading(true);
      await apiPostRequest(endpoints.rejectCommunityMember(selectedMember?.id));
      setRequests((prevMembers) =>
        prevMembers.filter((member) => member.id !== selectedMember.id),
      );
      showSnackbar(
        `${t("ConfirmationModal.text.has_been_rejected_part1")} ${
          selectedMember?.full_name
        } ${t("ConfirmationModal.text.has_been_rejected_part2")}`,
        "success",
      );
      getMemberStats();
    } catch (ex) {
      console.log("error occured while ignoring a request", ex);
    } finally {
      setIsModalOpen(false);
      setIsLoading(false);
      setSelectedMember(null);
    }
  };

  // Close Popup modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
    setActionType("");
  };

  // Open Popup modal or directly accept member
  const openModal = (member, action) => {
    setSelectedMember(member);
    if (action === "accept") {
      acceptMember(member);
    } else {
      setActionType(action);
      setIsModalOpen(true);
    }
  };

  // Choose the correct handler based on action type
  const handleAction = () => {
    if (actionType === "remove") removeMember();
    else if (actionType === "reject") rejectMember();
  };

  // sort members order by join date
  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const requestsData = useMemo(() => {
    return requests.sort((a, b) => {
      const dateA = parseDate(a.join_date);
      const dateB = parseDate(b.join_date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [requests, sortOrder]);

  const membersData = useMemo(() => {
    return members.sort((a, b) => {
      const dateA = parseDate(a.join_date);
      const dateB = parseDate(b.join_date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [members, sortOrder]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <MembersHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        memberStats={memberStats}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          flexBasis: 1,
          overflow: "hidden",
        }}
      >
        {activeTab === 1 && (
          <MyMembersCard
            {...{
              members: membersData,
              setMembers,
              openModal,
              activeTab,
              sortOrder,
              toggleSortOrder,
              isLoading,
              containerRef: setContainerRef,
              isModerator,
            }}
          />
        )}
        {activeTab === 2 && (
          <MyMembersCard
            {...{
              members: requestsData,
              openModal,
              activeTab,
              sortOrder,
              toggleSortOrder,
              isLoading,
              containerRef: setContainerRef,
              isModerator,
            }}
          />
        )}
      </Box>
      <ConfirmationModal
        open={isModalOpen}
        actionType={actionType}
        title={
          actionType === "remove"
            ? t("ConfirmationModal.title.remove")
            : t("ConfirmationModal.title.reject")
        }
        text={
          actionType === "remove"
            ? `${t("ConfirmationModal.text.remove_part1")}${
                selectedMember?.full_name
              } ${t("ConfirmationModal.text.remove_part2")}`
            : `${t("ConfirmationModal.text.reject")} ${
                selectedMember?.full_name
              }?`
        }
        okText={
          actionType === "remove"
            ? t("ConfirmationModal.ok_button_text.remove")
            : t("ConfirmationModal.ok_button_text.reject")
        }
        cancelText={t("ConfirmationModal.cancel_button")}
        handleOpen={handleAction}
        handleClose={closeModal}
      />
    </Box>
  );
}

export default CommunityMembers;
